/* The return record itself — `/sales/returns/:id`'s content, and later a drawer over the list
   reusing the same component (mirrors `orders/OrderDetail.js`). Takes finished data (`item`,
   already-loaded) and a `lang`; knows nothing about `:id`, the request, or the loading/error
   states — that is `Page.js`'s job.

   Read-only, same as orders: 1C/Excalibur is the source of truth, no dirty state, no save bar.

   Field shapes come from the ACTUAL `excalibur/returns/{id}/` response, not the written spec:
   there is no money anywhere (no `total`, no per-item price), no `reason` on an item, no
   `tracked_geolocation` (that field exists only on `Order`, so this page has no geolocation card
   at all — not even an empty one), no `related_order`, and no `department`. None of those are
   rendered here. `items_count`/`units_count` are on the LIST serializer only — the summary
   section below derives both from the loaded `item.items` instead of reading fields that are not
   in this response. */

import React from "react";
import { useHistory } from "react-router-dom";
import { Descriptions, Flex, Table, Tag, Typography } from "antd";
import { LinkOutlined, RollbackOutlined, SyncOutlined } from "@ant-design/icons";

import { KpiStrip } from "../../component/detail-page";
import { PersonCell, StatusIcon, dash, textOf } from "../../component/table-cells";
import { RETURNS_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { translate } from "../../util/util";

import { STATUS_ICON } from "./parts";

/** items/units, once — `ReturnDetail`'s items-table footer and the header's KPI strip both need
    it, and it has to agree with the footer it sits above, so it is not recomputed differently in
    two places. */
const itemTotals = item => ({
  itemsCount: item.items?.length || 0,
  unitsCount: (item.items || []).reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
});

/** The page head — entity icon, number, status, the 1C reference, and the KPI strip. Its own
    export because `Page.js` needs it OUTSIDE `dp-grid` (spec/03 archetype B), and because the
    list's record modal reuses it verbatim as the antd `Modal` `title` — one header for the record,
    not a page version and a separate, thinner modal version that drift apart. Icon is
    `RollbackOutlined`, the same glyph `AppNav.js` uses for the "Повернення" nav row, so the record
    reads as the same kind of thing whichever nav item led here. */
export function ReturnDetailHead({ item, lang }) {
  const t = key => translate(RETURNS_PAGE, key, lang);
  const { Icon, tone } = STATUS_ICON[item.status] || STATUS_ICON.new;
  const statusLabel = translate(RETURNS_PAGE, `return_status_${item.status}`, lang) || item.status;
  const { itemsCount, unitsCount } = itemTotals(item);

  return (
    <div className="dp-head dp-head--fluid">
      <div className="dp-head__body">
        <div className="dp-head__top">
          <div className={`dp-head__icon dp-head__icon--${tone}`}>
            <RollbackOutlined />
          </div>
          <div className="dp-head__main">
            <div className="dp-head__titlerow">
              <h1 className="dp-title">{item.name ? textOf(item.name) : `#${item.id}`}</h1>
              {/* The filled pill `visit/parts.js`'s `StatusTag` already established — a `-bg`
                 tint plus a `-700` label plus an icon, never colour alone (spec/01#status-colour).
                 Built inline rather than imported: that component reads its labels from
                 `VISIT_PAGE`. */}
              <Tag variant="filled" icon={<Icon />} className={`status-tag status-tag--${tone}`}>
                {statusLabel}
              </Tag>
              {/* Bordered, not filled — this is the record's id in the external system that feeds
                 ours, the same "foreign system data reads as a plain fact, never one of our
                 verdicts" split `OrderStatusTag` draws elsewhere in this app. The label names no
                 system: which one it is belongs to the deployment, not to the field. */}
              {item.external_id ? (
                <Tag icon={<SyncOutlined />}>
                  {t("k_external_id")}: {textOf(item.external_id)}
                </Tag>
              ) : null}
            </div>
            <Typography.Text type="secondary">
              {[item.datetime ? dayjs(item.datetime).format("DD.MM.YYYY HH:mm") : null, item.counterparty?.name]
                .filter(value => typeof value === "string" && value)
                .join(" · ")}
            </Typography.Text>
          </div>
        </div>
        {/* Always the same THREE tiles, an empty value shown as `dash` rather than one tile fewer.
            A strip whose tile count depends on the record reads as a rendering fault, and with
            `flex: 1 1 0` each surviving tile also takes the missing one's share — two tiles on a
            wide page stretched ~800px each, which is how the numbers got lost in the first place.
            `date_of_physical_return` is empty on 16 of 30 returns on dev and across every status
            (10 of 16 `new` have it, 2 of 3 `closed` do NOT), so it is genuinely "not recorded",
            not "not yet returned" — there is no honest substitute figure to put here, and
            `dash` is `--neutral-300`, quiet enough not to read as a zero. */}
        <KpiStrip
          items={[
            { key: "positions", label: translate(RETURNS_PAGE, "k_positions_units", lang), value: `${itemsCount} / ${unitsCount}` },
            {
              key: "return_date",
              label: translate(RETURNS_PAGE, "k_return_date", lang),
              value: item.datetime ? dayjs(item.datetime).format("DD.MM.YYYY") : dash,
            },
            {
              key: "physical_date",
              label: translate(RETURNS_PAGE, "k_physical_return_date", lang),
              value: item.date_of_physical_return ? dayjs(item.date_of_physical_return).format("DD.MM.YYYY") : dash,
            },
          ]}
        />
      </div>
    </div>
  );
}

/* `additional_info` is not shown here — the field arrives as `{}` on most records (nothing
   anyone wrote), and where it does carry something it has never been more than duplicate
   metadata for a supervisor deciding on a return. `comments` is the one that carries the actual
   reason for the return ("пошкоджена упаковка при транспортуванні"), so it earns the visual
   weight of its own box below the field grid instead of becoming row 12 of 12. */
const hasComment = item => Boolean(item.comments);

const ReturnDetail = ({ item, lang, showHeader = true }) => {
  const history = useHistory();
  const t = key => translate(RETURNS_PAGE, key, lang);

  const { Icon: rowIcon, tone: rowTone } = STATUS_ICON[item.status] || STATUS_ICON.new;
  const statusLabel = translate(RETURNS_PAGE, `return_status_${item.status}`, lang) || item.status;

  const { itemsCount, unitsCount } = itemTotals(item);

  const itemColumns = [
    {
      key: "name",
      title: t("k_product_name"),
      ellipsis: true,
      render: (value, row) => textOf(row.product?.name),
    },
    { key: "code", title: t("k_product_code"), width: 160, render: (value, row) => textOf(row.product?.code) },
    {
      key: "quantity",
      title: t("k_quantity"),
      width: 110,
      align: "right",
      render: (value, row) => <span className="lp-num">{textOf(row.quantity)}</span>,
    },
  ];

  return (
    <>
      {showHeader ? <ReturnDetailHead item={item} lang={lang} /> : null}

      <div className="dp-card">
        <Typography.Text strong className="dp-sec-title">
          {t("sec_general")}
        </Typography.Text>
        {/* Two columns, row-major (antd fills left-to-right then wraps): pairs are chosen for
           meaning, not to bisect the field list — entity/location, event/people, status/dates,
           audit dates. `{xs:1, sm:1, md:2}` collapses to one column below 768px, which is also
           where `dp-grid` itself stacks the rail, so page and modal agree at every width that
           matters. */}
        <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small" colon={false} className="dp-desc">
          <Descriptions.Item label={t("k_counterparty")}>
            {item.counterparty ? (
              <Typography.Text>
                {textOf(item.counterparty.name)}
                {item.counterparty.address ? <Typography.Text type="secondary"> · {textOf(item.counterparty.address)}</Typography.Text> : null}
              </Typography.Text>
            ) : (
              dash
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t("k_point")}>
            {item.point ? (
              <Typography.Text>
                {textOf(item.point.name)}
                {item.point.address ? <Typography.Text type="secondary"> · {textOf(item.point.address)}</Typography.Text> : null}
              </Typography.Text>
            ) : (
              dash
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t("k_visit")}>
            {item.visit ? (
              <Typography.Link onClick={() => history.push(`/visit/${item.visit}`)}>
                {t("k_visit")} <LinkOutlined />
              </Typography.Link>
            ) : (
              dash
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t("k_created_by")}>{item.created_by ? <PersonCell user={item.created_by} strong={false} /> : dash}</Descriptions.Item>
          <Descriptions.Item label={t("k_supervisor")}>{item.supervisor ? <PersonCell user={item.supervisor} strong={false} /> : dash}</Descriptions.Item>
          {/* ⚠ `Descriptions.Item`'s content wrapper is `display: flex` (antd, confirmed via
             getComputedStyle) — a literal space character between two flex children is a
             whitespace-only text node, which flexbox drops from layout entirely. An explicit
             `Flex gap` is what actually reserves the space; the icon and the label rendered
             touching without one. Grepped both `orders/` and `returns/` for the same
             space-as-separator pattern — every other instance sits inside a `display: block`
             parent (`Typography.Link`'s "Візит" + icon), where a literal space still renders. */}
          <Descriptions.Item label={t("k_status")}>
            <Flex align="center" gap={6}>
              <StatusIcon Icon={rowIcon} tone={rowTone} label={statusLabel} />
              <Typography.Text>{statusLabel}</Typography.Text>
            </Flex>
          </Descriptions.Item>
          <Descriptions.Item label={t("k_return_date")}>
            <span className="dp-nowrap">{item.datetime ? dayjs(item.datetime).format("DD.MM.YYYY HH:mm") : dash}</span>
          </Descriptions.Item>
          <Descriptions.Item label={t("k_physical_return_date")}>
            <span className="dp-nowrap">{item.date_of_physical_return ? dayjs(item.date_of_physical_return).format("DD.MM.YYYY") : dash}</span>
          </Descriptions.Item>
          <Descriptions.Item label={t("k_created")}>
            <span className="dp-nowrap">{item.created ? dayjs(item.created).format("DD.MM.YYYY HH:mm") : dash}</span>
          </Descriptions.Item>
          <Descriptions.Item label={t("k_modified")}>
            <span className="dp-nowrap">{item.modified ? dayjs(item.modified).format("DD.MM.YYYY HH:mm") : dash}</span>
          </Descriptions.Item>
        </Descriptions>
        {/* A comment carries the reason for the return, which reads as content, not metadata; a
           grey block under its own label keeps it visually distinct from the field grid instead
           of becoming row 12 of 12. Mirrors `docs/design/return-page.html`'s own `#p-info`
           comment block (same card, below the field grid). */}
        {hasComment(item) ? (
          <div className="dp-notes">
            <Typography.Text type="secondary" className="dp-note__label">
              {t("k_comment")}
            </Typography.Text>
            <div className="dp-note__box">{textOf(item.comments)}</div>
          </div>
        ) : null}
      </div>

      {/* Totals live as a footer of THIS table, not a separate "Підсумки" card — per
         `docs/design/return-page.html`'s own `#p-items`, where `.totbox` is a div right under
         `<table>` inside the same items container. `TableFoot` (`table-cells.js`) does not fit
         this: its signature is fixed to page/size/total/onPage/onSize for a PAGINATION footer,
         with no slot for arbitrary content — a real mismatch, not a stylistic choice.

         antd's own `Table.Summary` — a real `<tfoot>`, bordered and column-aligned like the body
         — is the better fit than a freestanding block below the table: the total READS as part of
         the table (it sits directly under "Кількість", the column it sums) instead of a heading
         that merely happens to be nearby. Two summary rows mirror the body rows' own two-line
         rhythm; only the second one is emphasized, since "Одиниць до повернення" is the number
         someone opens this card to find. */}
      <div className="dp-card">
        <Typography.Text strong className="dp-sec-title">
          {t("sec_items")}
        </Typography.Text>
        <Table
          rowKey="id"
          size="middle"
          columns={itemColumns}
          dataSource={item.items || []}
          pagination={false}
          scroll={{ x: 400 }}
          locale={{ emptyText: t("no_items") }}
          summary={() => (
            /* A `border-top` + a subtle tint on the summary rows — without them the two rows are
               pixel-identical to a body row and read as two more items in the list, not a total.
               `.dp-summary-cell` (`detail-page.scss`, shared with `OrderDetail`'s own summary
               row) carries the border as a class, not a `style` prop — see that file for why. */
            <Table.Summary fixed="bottom">
              <Table.Summary.Row style={{ background: "var(--surface-subtle)" }}>
                <Table.Summary.Cell index={0} colSpan={2} className="dp-summary-cell">
                  <Typography.Text type="secondary">{t("k_items_count")}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right" className="dp-summary-cell">
                  <span className="lp-num">{itemsCount}</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row style={{ background: "var(--surface-subtle)" }}>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Typography.Text strong>{t("k_units_to_return")}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  {/* The one deliberately larger number in this card — 16px is `dp-sec-title`'s
                     own step, not an invented size, kept inside the table's own row height rather
                     than a heading-level size that would fight the summary row's compact grid. */}
                  <Typography.Text strong className="lp-num" style={{ fontSize: 16 }}>
                    {unitsCount}
                  </Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </div>
    </>
  );
};

export default ReturnDetail;
